import { useState } from "react";
import type { Device, PortalApi } from "../api.ts";
import type { Identity } from "../auth.ts";
import { shortId } from "../Id.tsx";
import { LocalTime, TimeZoneNote } from "../LocalTime.tsx";
import { messageOf, useLoad } from "../load.ts";
import { Badge, ConfirmButton, Notice, PageLayout, Table } from "../ui.tsx";

/**
 * Devices (docs/credentials.md#devices), for every role: a device is one installation of the app on
 * one machine, signed in as one producer, that saved passwords are encrypted to. Members see and
 * revoke their own; admins and operators every producer's. The data API answers each caller with
 * the devices they may see, so the page lists what it gets.
 */

/** Whether a role sees and revokes every producer's devices. */
export function seesEveryDevice(role: Identity["role"]): boolean {
  return role === "admin" || role === "operator";
}

export function DevicesPage({ api, identity }: { api: PortalApi; identity: Identity }) {
  const list = useLoad(() => api.devices(), [api]);
  const [notice, setNotice] = useState<{ text: string; error?: boolean }>();
  const every = seesEveryDevice(identity.role);

  const revoke = async (device: Device) => {
    setNotice(undefined);
    try {
      const revoked = await api.revokeDevice(device.id);
      list.set((list.data ?? []).map((other) => (other.id === revoked.id ? revoked : other)));
      setNotice({
        text: `Revoked ${device.name}: it can no longer fetch saved logins. The app on it registers again only once signed out and in.`,
      });
    } catch (error) {
      setNotice({ text: messageOf(error), error: true });
    }
  };

  return (
    <PageLayout title="Devices">
      <p className="max-w-[80ch]">
        {every ? "Every producer's devices." : "Your devices."} A device is one installation of the
        Quote Agent app on one machine, signed in as one producer; it registers itself, and saved
        passwords are encrypted to it alone. Revoke one that is lost, sold or not yours.
      </p>
      {notice && (
        <Notice tone={notice.error ? "bad" : "ok"} role="status">
          {notice.text}
        </Notice>
      )}
      {list.loading && <p className="text-muted">Loading…</p>}
      {list.error && <Notice tone="bad">{list.error}</Notice>}
      {list.data && list.data.length === 0 && <p className="text-muted">No devices.</p>}
      {list.data && list.data.length > 0 && (
        <>
          <Table compact>
            <thead>
              <tr>
                <th>Device</th>
                {every && <th>Producer</th>}
                <th>App</th>
                <th>Registered</th>
                <th>Last used</th>
                <th>Revoked</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.data.map((device) => (
                <tr key={device.id}>
                  <td title={`Key fingerprint ${device.fingerprint}`}>{device.name}</td>
                  {every && (
                    <td>
                      {device.email ?? (
                        <span title={device.producerId}>{shortId(device.producerId)}</span>
                      )}
                    </td>
                  )}
                  <td>{device.app}</td>
                  <td>
                    <LocalTime iso={device.registeredAt} />
                  </td>
                  <td>
                    {device.lastUsedAt ? (
                      <LocalTime iso={device.lastUsedAt} />
                    ) : (
                      <span className="text-muted">never</span>
                    )}
                  </td>
                  <td>
                    {device.revoked ? (
                      <Badge tone="bad">
                        {device.revokedAt ? <LocalTime iso={device.revokedAt} /> : "revoked"}
                      </Badge>
                    ) : (
                      <span className="text-muted">no</span>
                    )}
                  </td>
                  <td>
                    {!device.revoked && (
                      <ConfirmButton
                        label="Revoke"
                        question={`Revoke ${device.name}?`}
                        confirm="Yes, revoke it"
                        onConfirm={() => revoke(device)}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <TimeZoneNote />
        </>
      )}
    </PageLayout>
  );
}
